import type { Message, Reaction, UserId } from '@cyphex/types';

/**
 * WHAT IS MINE STAYS MINE.
 *
 * A realtime frame is ONE copy sent to everybody who has the conversation open,
 * and the message in it was read for whoever caused it: an edit carries its
 * editor's view, a reaction carries the reactor's, and a meeting card that
 * closes itself (`closeChannelAnnouncement`) carries nobody's. But some of what
 * is on a message is not about the message - it is about the person looking at
 * it: whether they starred it, bookmarked it, set a reminder on it, which of
 * its reactions are theirs, which options of its poll they chose.
 *
 * Taken from the frame, those showed every reader somebody else's: one person
 * reacts and the pill lights up as "yours" for the whole room; somebody edits a
 * message you starred and your star goes out. They are kept from the copy this
 * client already holds. Each has its own event when it really changes
 * (`message.saved`, `message.reminder.set`, `reaction.added`, `poll.updated`).
 */
export function keepViewerState(mine: Message, incoming: Message): Message {
  return {
    ...incoming,
    isBookmarked: mine.isBookmarked,
    isSaved: mine.isSaved,
    reminderAt: mine.reminderAt,
    reactions: withMyReactions(mine.reactions, incoming.reactions),
    poll:
      incoming.poll === null || mine.poll === null || mine.poll.id !== incoming.poll.id
        ? incoming.poll
        : { ...incoming.poll, myVotes: mine.poll.myVotes },
  };
}

/** The counts are the frame's; which of them are mine is this client's. */
function withMyReactions(mine: readonly Reaction[], incoming: readonly Reaction[]): Reaction[] {
  const reacted = new Set(mine.filter((reaction) => reaction.reacted).map((reaction) => reaction.emoji));
  return incoming.map((reaction) => ({ ...reaction, reacted: reacted.has(reaction.emoji) }));
}

/**
 * The pill row after somebody reacted.
 *
 * The row in the event was read for the person who reacted. When that is this
 * viewer - on this device or another of theirs - it is exactly right and is
 * taken whole. For everyone else only the counts are news.
 */
export function reactionsForViewer(
  mine: readonly Reaction[],
  event: { readonly userId: UserId; readonly reactions: readonly Reaction[] },
  viewerId: UserId | null,
): Reaction[] {
  if (viewerId !== null && event.userId === viewerId) return [...event.reactions];
  return withMyReactions(mine, event.reactions);
}
